import React from 'react';
import Modal from 'modules/views/shared/Modal';
import { SkippedFieldInfo } from '../hooks/useSkippedFieldsCheck';
import { SkippableTillType } from 'utils/skippableFieldsUtils';

// Helper component for displaying project information
interface ProjectInfoProps {
  title: string;
  value: string;
  className: string;
  labelClass: string;
  valueClass: string;
}

export const ProjectInfo: React.FC<ProjectInfoProps> = ({ title, value, className, labelClass, valueClass }) => (
  <div className={className}>
    <p className={labelClass}>{title}</p>
    <p className={valueClass}>{value}</p>
  </div>
);

// Modal for consent form required fields that were skipped
interface SkippedFieldsModalProps {
  isVisible: boolean;
  fieldNames: string[];
  onNavigate: () => void;
  onClose: () => void;
  classes: {
    skippedFieldsMessage: string;
    highlightedField: string;
  };
  t: (key: string, defaultValue?: string) => string;
}

export const SkippedFieldsModal: React.FC<SkippedFieldsModalProps> = ({
  isVisible,
  fieldNames,
  onNavigate,
  onClose,
  classes,
  t,
}) => {
  const message = t('consentForm.skippedFields.message');
  const [beforeFields, afterFields] = message.split('{{fields}}');

  return (
    <Modal
      isVisible={isVisible}
      title={t('consentForm.skippedFields.title')}
      Content={
        <p className={classes.skippedFieldsMessage}>
          {beforeFields}
          <span className={classes.highlightedField}>{fieldNames.join(', ')}</span>
          {afterFields || ''}
        </p>
      }
      actionButtons={[
        {
          label: t('consentForm.skippedFields.back'),
          onClick: onNavigate,
          testId: 'skipped-fields-back-button',
        },
      ]}
      onClose={onClose}
    />
  );
};

// Modal for badge-related fields that are optional until badge operations
interface OptionalBadgeFieldsModalProps {
  isVisible: boolean;
  skippedFields: SkippedFieldInfo[];
  onGoBack: () => void;
  onContinue: () => void;
  onClose: () => void;
  classes: {
    skippedFieldsMessage: string;
    highlightedField: string;
    badgeModalParagraph: string;
    badgeModalList: string;
    badgeModalListItem: string;
  };
  t: (key: string, defaultValue?: string) => string;
}

export const OptionalBadgeFieldsModal: React.FC<OptionalBadgeFieldsModalProps> = ({
  isVisible,
  skippedFields,
  onGoBack,
  onContinue,
  onClose,
  classes,
  t,
}) => {
  const getSkippableTillText = (skippableTill: SkippableTillType): string => {
    if (skippableTill === SkippableTillType.BadgePrinting) {
      return 'Badge Printing';
    }
    if (skippableTill === SkippableTillType.BadgeActivation) {
      return 'Badge Activation';
    }
    return '';
  };

  return (
    <Modal
      isVisible={isVisible}
      title={t('common.warning')}
      Content={
        <div className={classes.skippedFieldsMessage}>
          <ul className={classes.badgeModalList}>
            {skippedFields.map((field, index) => (
              <li key={index} className={classes.badgeModalListItem}>
                <span className={classes.highlightedField}>{field.fieldName}</span>
                {' is required but skippable until '}
                <strong>{getSkippableTillText(field.skippableTill)}</strong>
              </li>
            ))}
          </ul>
          <p>{t('consentForm.badgeSkippable.provideNowOrLater')}</p>
        </div>
      }
      actionButtons={[
        {
          label: t('consentForm.badgeSkippable.goBack'),
          variant: 'contained' as const,
          onClick: onGoBack,
          testId: 'badge-fields-go-back-button',
        },
        {
          label: t('common.next'),
          variant: 'outlined' as const,
          onClick: onContinue,
          hasEndArrow: true,
          testId: 'badge-fields-continue-button',
        },
      ]}
      onClose={onClose}
    />
  );
};
